import { memo, useEffect, useRef, useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Editor, EditorInstance } from '@shared/components/Editor'
import { LoadingIndicator, NotFound } from '@shared/layouts'
import { useAvcInstanceTaskUpdateMutation } from 'hooks/mutations/content'
import { avcInstanceTasksRoute } from 'pages/NWPlatform/AvcSystem/Instances/Instance'
import { rawRoute } from 'pages/NWPlatform/AvcSystem/Instances/Instance/Task'
import { avcInstanceTaskQueryKeys } from 'services/queryKeys'

const JSONRaw = () => {
  const { t } = useTranslation(['common', 'domain'])
  const { enqueueSnackbar } = useSnackbar()

  const editorInstanceRef = useRef<EditorInstance>(undefined)

  const navigate = useNavigate({ from: rawRoute.fullPath })

  const { avcInstanceId: instanceId, avcInstanceTaskId } = rawRoute.useParams()

  const [saveAndExit, setSaveAndExit] = useState(false)

  const avcInstanceTaskQuery = useQuery(avcInstanceTaskQueryKeys.detail({ instanceId, taskId: avcInstanceTaskId }))
  const { data: avcInstanceTask } = avcInstanceTaskQuery

  const { mutate } = useAvcInstanceTaskUpdateMutation()

  useEffect(() => {
    if (editorInstanceRef.current && avcInstanceTaskQuery.isSuccess && !!avcInstanceTask?.config) {
      editorInstanceRef.current.getModel()?.setValue(window.JSON.stringify(avcInstanceTask.config, null, 4))
    }
  }, [avcInstanceTask, avcInstanceTaskQuery.isSuccess])

  if (avcInstanceTaskQuery.isLoading) {
    return <LoadingIndicator />
  }

  if (!avcInstanceTask) {
    return <NotFound />
  }

  return (
    <Editor
      value={avcInstanceTask.config}
      onSubmit={(data) => {
        mutate(
          {
            taskId: avcInstanceTaskId,
            instanceId,
            avcInstanceTaskConfigGenericDistributedTaskEdit: { ...avcInstanceTask, config: data },
          },
          {
            onSuccess: () => {
              enqueueSnackbar(t('domain:ContentProcessing.Instance.avcInstanceTaskSaved'), {
                variant: 'success',
              })
              if (saveAndExit) {
                setSaveAndExit(false)
                navigate({ to: avcInstanceTasksRoute.to, search: { query: '' } })
              }
            },
          },
        )
      }}
      getEditorInstance={(editorInstance: EditorInstance) => void (editorInstanceRef.current = editorInstance)}
    />
  )
}

export const JSON = memo(JSONRaw)
